/**
 * Rehab roll-up for one property's header card — estimated budget vs. actual
 * spend, from the `rehab_projects` row. Read-only.
 */
import { useQuery } from "@tanstack/react-query";
import type { PostgrestSingleResponse } from "@supabase/supabase-js";

import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/hooks/use-workspace";

export interface RehabSummary {
  estimated: number;
  actual: number;
  isComplete: boolean;
  completedAt: string | null;
}

interface RehabProjectRow {
  total_budget: number | null;
  total_spent: number | null;
  stage: string | null;
  actual_completion: string | null;
}

export function usePropertyRehab(pipelinePropertyId?: string | null) {
  const { currentWorkspace } = useWorkspace();
  const workspaceId = currentWorkspace?.id;

  const query = useQuery({
    queryKey: ["property-rehab", pipelinePropertyId] as const,
    enabled: Boolean(workspaceId && pipelinePropertyId),
    queryFn: async (): Promise<RehabSummary | null> => {
      if (!workspaceId || !pipelinePropertyId) return null;
      const { data, error } = (await supabase
        .from("rehab_projects")
        .select("total_budget, total_spent, stage, actual_completion")
        .eq("workspace_id", workspaceId)
        .eq("pipeline_property_id", pipelinePropertyId)
        .order("actual_completion", { ascending: false })
        .limit(1)
        .maybeSingle()) as PostgrestSingleResponse<RehabProjectRow>;
      if (error) throw error;
      if (!data) return null;
      return {
        estimated: Number(data.total_budget) || 0,
        actual: Number(data.total_spent) || 0,
        isComplete: data.stage === "completed",
        completedAt: data.actual_completion,
      };
    },
  });

  return { rehab: query.data ?? null, isLoading: query.isLoading };
}
